import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { ConfigProvider, App, Button, message } from 'antd'
import { Outlet, useLocation, Link } from 'react-router-dom'
import { useAtom } from 'jotai'
import { LogoutOutlined } from '@ant-design/icons'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import axios from 'axios'
import zhCN from 'antd/locale/zh_CN.js'
import type { Locale } from 'antd/lib/locale/index.js'
import { appApiUrls, appPageUrls } from '../shared/url-map.js'
import { getAntdConfigProviderTheme } from './utils.js'
import { themeAtom } from './atoms.js'

const navLinks = [
  { to: appPageUrls.usage, label: '使用概况' },
  { to: appPageUrls.locationDashboard, label: '位置分析' },
  { to: appPageUrls.timelineImages, label: '时间轴图片' },
  { to: appPageUrls.scanImages, label: '识物图片' },
  { to: '/admin/errors', label: '错误监控' },
  { to: appPageUrls.revenueCat, label: 'RevenueCat' },
  { to: '/admin/trackpoints', label: 'GPS 2D' },
  { to: '/admin/trackpoints-3d', label: 'GPS 3D' },
] as const

const gpsDebugPaths = new Set(['/admin/trackpoints', '/admin/trackpoints-3d'])

export default function LayoutAdmin() {
  const [theme, _setTheme] = useAtom(themeAtom)
  const location = useLocation()
  const isAdminPage = location.pathname.startsWith('/admin')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [currentSearch, setCurrentSearch] = useState(() => window.location.search)

  useEffect(() => {
    const syncSearch = () => setCurrentSearch(window.location.search)
    window.addEventListener('popstate', syncSearch)
    window.addEventListener('deergo:url-sync', syncSearch)
    return () => {
      window.removeEventListener('popstate', syncSearch)
      window.removeEventListener('deergo:url-sync', syncSearch)
    }
  }, [])

  const handleLogout = async () => {
    try {
      await axios.post(appApiUrls.auth.logout)
      message.success('已登出')
      window.location.reload()
    } catch (error) {
      console.error('登出失败:', error)
      message.error('登出失败')
    }
  }

  const getLinkClassName = (path: string) =>
    clsx(
      'rounded-md px-3 py-2 text-sm font-medium transition-colors',
      location.pathname === path
        ? 'bg-blue-500 text-white'
        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
    )

  const getLinkTo = (path: string) => {
    if (gpsDebugPaths.has(path) && gpsDebugPaths.has(location.pathname)) return `${path}${currentSearch}`
    return path
  }

  return (
    <ConfigProvider locale={zhCN as unknown as Locale} theme={getAntdConfigProviderTheme(theme)}>
      <App>
        <div
          className={clsx(
            'layout-root relative flex min-h-screen flex-col text-black dark:bg-slate-900 dark:text-slate-100',
          )}
        >
          <header>
            <div className="flex items-center justify-between bg-white p-4 dark:bg-slate-800">
              <div className="flex items-center">
                <h1 className="text-lg font-bold">DeerGo Server</h1>
              </div>
              {isAdminPage && (
                <>
                  {/* Desktop nav */}
                  <div className="hidden items-center gap-4 md:flex">
                    <nav className="flex gap-4">
                      {navLinks.map((link) => (
                        <Link key={link.to} to={getLinkTo(link.to)} className={getLinkClassName(link.to)}>
                          {link.label}
                        </Link>
                      ))}
                    </nav>
                    <Button
                      type="text"
                      icon={<LogoutOutlined />}
                      onClick={handleLogout}
                      className="text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      登出
                    </Button>
                  </div>
                  {/* Mobile hamburger button */}
                  <button
                    className="rounded-md p-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 md:hidden"
                    onClick={() => setMobileMenuOpen(true)}
                    aria-label="打开导航菜单"
                    aria-expanded={mobileMenuOpen}
                  >
                    <Bars3Icon className="h-6 w-6" />
                  </button>
                </>
              )}
            </div>
          </header>

          {/* Mobile drawer overlay */}
          {mobileMenuOpen && (
            <div className="fixed inset-0 z-50 md:hidden">
              {/* Backdrop */}
              <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
              {/* Drawer */}
              <div className="fixed right-0 top-0 flex h-full w-64 flex-col bg-white shadow-xl dark:bg-slate-800">
                <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
                  <span className="text-lg font-bold">菜单</span>
                  <button
                    className="rounded-md p-1 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-label="关闭导航菜单"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <nav className="flex flex-1 flex-col gap-1 p-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={getLinkTo(link.to)}
                      className={clsx(getLinkClassName(link.to), 'block')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>
                <div className="border-t border-gray-200 p-4 dark:border-gray-700">
                  <Button
                    type="text"
                    icon={<LogoutOutlined />}
                    onClick={() => {
                      setMobileMenuOpen(false)
                      handleLogout()
                    }}
                    className="w-full text-left text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    登出
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-img absolute top-0 -z-10 flex h-full w-full place-items-center overflow-hidden bg-white dark:bg-slate-900 md:bg-[#F6F5FA] dark:md:bg-slate-900" />
          <main className="flex-1 overflow-y-auto">
            <Outlet />
          </main>
          <footer className="bg-white p-4 dark:bg-slate-800">
            <div className="flex items-center justify-between">
              <div>
                <p>&copy; {new Date().getFullYear()} DeerGo</p>
              </div>
              <div>
                <p>备案号：浙ICP备2024122672号</p>
              </div>
            </div>
          </footer>
        </div>
      </App>
    </ConfigProvider>
  )
}
